import { SelectField, Submit, useForm } from '@ff-it/form';
import { activityFieldProps, useActivities } from 'components';
import { ProductTitle, type EmbeddedProduct } from 'modules/supplier/products';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Activity, EmbeddedCompany } from 'types';
import * as styles from './SupplierForm.css';

import { CompanyField, companyFieldProps } from 'modules/core/company/components';
import { SelectInstance, StylesConfig } from 'react-select';

import useSWR from 'swr';
import { palette } from '@ff-it/ui';

import { ChainTargetEntry, productToChainPath } from '../ProductChainPicker';

type FormData = {
  activity: Activity | null;
  provider: EmbeddedCompany | null;
};

type SupplierFormProps = {
  product: EmbeddedProduct | null;
  for_block?: number;
  setProduct: (p: EmbeddedProduct | null) => void;
  providerId: number | undefined;
  required?: boolean;
  partial?: boolean;
};

type MaybeSuggestedProvider = EmbeddedCompany & {
  _block_usage?: number;
};

const providerStyles: StylesConfig<MaybeSuggestedProvider, false, any> = {
  option: (base, state) => ({
    ...base,
    ...(state.data._block_usage !== undefined && state.data._block_usage < 0 && !state.isSelected
      ? { color: palette.gray400 }
      : {}),
  }),
};

export function SupplierForm({
  product,
  for_block,
  providerId,
  required,
  partial,
  setProduct,
}: SupplierFormProps): ReactNode {
  const providerFieldRef = useRef<SelectInstance<any>>(null);

  const isFirstRender = useRef<boolean>(true);
  const first = isFirstRender.current;
  isFirstRender.current = false;
  const form = useForm<FormData>();

  const allActivities = useActivities();
  const activities = product?.activities.length
    ? product.activities
    : allActivities?.filter((a) => a.kind === product?.kind) || [];

  // We fetch providers for this product even when filter provider is present.
  // Product provider relationship is compilcated we do this jsut to validate it.
  const { data: productProviders, isLoading } = useSWR<MaybeSuggestedProvider[]>(
    product
      ? {
          url: `supplier/providers/`,
          method: 'GET',
          queryParams: { include_branches: true, product: product.id, for_block },
        }
      : null,
  );

  const providerOptions = useMemo(
    () => (providerId ? productProviders?.filter((p) => p.id === providerId) : productProviders),
    [productProviders, providerId],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: activities are in sync with product and form is stable
  useEffect(() => {
    if (first) {
      return;
    }

    let activity: Activity | null = null;
    if (product) {
      if (activities.length === 1) {
        activity = activities[0];
      } else if (product.kind === 'MEDIA') {
        // first standard
        activity = activities.find((a) => !a.non_standard) || null;
      }
    }
    // We reset activity always if product changes
    form.change('activity', activity);
  }, [product]);

  // Only re-runs when the provider list changes reference: initial load and product/for_block change.
  // A manual selection doesn't refetch, so it won't retrigger this and get overwritten.
  // biome-ignore lint/correctness/useExhaustiveDependencies: form is stable, product in sync
  useEffect(() => {
    // Skip the mount render so a pre-filled provider (edit) survives until data actually loads.
    if (first) {
      return;
    }

    const currentProvider = form.getFieldState('provider')?.value;

    // Clear provider if no product selected. Runs on the product transition
    // itself so every clear path (summary icon, chain picker) behaves the same,
    // even while the provider options are absent or still loading.
    if (!product) {
      if (currentProvider) {
        form.change('provider', null);
      }
      return;
    }

    // providers are undefined while loading we don't want extra renders just bail
    if (!providerOptions) {
      return;
    }

    // Options arrive pre-sorted by _block_usage desc from the API.
    // Auto-select when there's a single option or the top one clearly beats the runner-up.
    if (providerOptions.length > 0) {
      const top = providerOptions[0];
      const topUsage = top._block_usage ?? 0;
      const runnerUpUsage = providerOptions.length > 1 ? (providerOptions[1]._block_usage ?? 0) : 0;
      if (providerOptions.length === 1 || topUsage > runnerUpUsage) {
        if (currentProvider?.id !== top.id) {
          form.change('provider', top);
        }
        return;
      }
    }

    // Keep current provider if it's still valid
    if (currentProvider && providerOptions.find((p) => p.id === currentProvider.id)) {
      return;
    }

    // Clear invalid provider
    if (currentProvider) {
      form.change('provider', null);
    }

    // Focus field regardless of current provider value
    providerFieldRef.current?.focus();
  }, [productProviders, product]);

  // Product not set and form is partial. Pick any activity and any supplier
  if (partial && !product) {
    return (
      <div className={styles.container}>
        <div className={styles.product}>
          <div>No product selected</div>
        </div>
        <SelectField
          label="Activity"
          name="activity"
          menuPortalTarget={document.body}
          // if partai and product is not Set
          options={allActivities}
          {...activityFieldProps}
          openMenuOnFocus
        />
        {/* this has no product and for_block filters */}
        <CompanyField
          label="Provider"
          name="provider"
          menuPortalTarget={document.body}
          selectRef={providerFieldRef as any}
          openMenuOnFocus
          includeBranches
        />

        <Submit className="mt-auto" />
      </div>
    );
  }

  // product set and fields contrained or prdocut not set buty required
  const clearProduct = Boolean(product && (!required || partial));

  return (
    <div className={styles.container}>
      <ChainTargetEntry
        className={styles.productEntry}
        value={product ? { kind: 'product', id: product.id } : null}
        path={product ? productToChainPath(product) : undefined}
        renderTarget={product ? <ProductTitle product={product} size="md" /> : undefined}
        emptyMessage="No product selected"
        onClear={
          clearProduct
            ? () => {
                setProduct(null);
                form.change('provider', null);
              }
            : undefined
        }
      />
      <SelectField
        label="Activity"
        name="activity"
        menuPortalTarget={document.body}
        options={activities}
        {...activityFieldProps}
        disabled={!product}
        openMenuOnFocus
        required
      />

      <SelectField<MaybeSuggestedProvider>
        label="Provider"
        name="provider"
        menuPortalTarget={document.body}
        options={providerOptions}
        {...companyFieldProps}
        disabled={!product}
        selectRef={providerFieldRef}
        openMenuOnFocus
        isLoading={isLoading}
        required
        styles={providerStyles}
      />

      <Submit className="mt-auto" />
    </div>
  );
}
